import BokbiStatisticsFilter from './filter/BokbiStatisticsFilter';
import BokbiBarChart from './graph/BokbiBarChart';
import BokbiPieChart from './graph/BokbiPieChart';
import {
  useBokbiHistoryStore,
  IBokbiStatistics,
} from '../../../store/useBokbiHistory';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import queryString from 'query-string';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { useLoginStore } from '../../../store/useLogin';
import { useFranchiseStore } from '../../../store/useFranchise';
import { KRW_EXCHANGE_RATE, costInKrw, averageCost, sumCosts, formatCost } from '../../../util/orderStatisticsCost';

export interface IBokbiPieData {
  name: string;
  value: number;
}

export interface BokbiPieChartProps {
  annualRawData: IBokbiStatistics[];
  title: string;
}

// 주문 통계 타입 정의
interface IStatisticsProduct {
  confirmDt: string;
  productId: string;
  productName: string;
  totalCount: number;
  totalPromptToken: number;
  totalCompletionToken: number;
}

interface IStatisticsResponse {
  confirmDt: string;
  totalCount: number;
  totalPromptToken: number;
  totalCompletionToken: number;
  statisticsList: IStatisticsProduct[];
}

const BokbiStatistics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPeriod, setSelectedPeriod] = useState<
    'week' | 'month' | '90days' | 'year'
  >('month');
  const [orderStatistics, setOrderStatistics] = useState<IStatisticsResponse[]>(
    [],
  );
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  // 사용자 권한 확인
  const { role } = useLoginStore();
  const isAdmin = role === 'ROLE_ADMIN';

  // 프랜차이즈 목록 조회
  const { franchises, getFranchise } = useFranchiseStore();

  const {
    getBokbiStatistics,
    bokbiStatistics,
    getBokbiStatisticsLoading,
  } = useBokbiHistoryStore();

  // formatted dates를 useMemo로 메모이제이션
  const memoizedDates = useMemo(
    () => ({
      todayDate: formatDate(new Date())['yyyy-mm-ddTHH:mm:ss'],
      thirtyDaysAgoDate: formatDate(
        (() => {
          const date = new Date();
          date.setDate(date.getDate() - 30);
          return date;
        })(),
      )['yyyy-mm-ddTHH:mm:ss'],
    }),
    [],
  ); // 한 번만 계산

  // 주문 통계 데이터 조회
  const getOrderStatistics = useCallback(
    async (startDt: string, endDt: string, franchiseId?: string) => {
      setIsLoadingStatistics(true);
      try {
        // 날짜 형식 변환 (yyyy-mm-dd 형식으로)
        const startDate = startDt ? startDt.split('T')[0] : '';
        const endDate = endDt ? endDt.split('T')[0] : '';

        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        // 프랜차이즈 ID 사용 (파라미터로 전달된 값 우선)
        if (franchiseId) params.append('franchiseId', franchiseId);

        const url = isAdmin
          ? `/contract/statistics/token?${params.toString()}`
          : `/contract/statistics?${params.toString()}`;

        const response = await apiClient.get(url);
        setOrderStatistics(response.data || []);
      } catch (error) {
        if (isAxiosError(error)) {
          toast.error(error.response?.data?.message || '통계 조회 실패');
        }
        setOrderStatistics([]);
      } finally {
        setIsLoadingStatistics(false);
      }
    },
    [isAdmin],
  );

  // 필터 변경 시 URL 업데이트만 수행
  const getFilterdBokbiData = useCallback(
    (params?: any) => {
      const parsedQuery = queryString.parse(location.search);
      const franchiseId =
        params?.franchiseId !== undefined
          ? params.franchiseId
          : (parsedQuery.franchiseId as string) || '';
      const startDt =
        params?.startDt ||
        (parsedQuery.startDt as string) ||
        memoizedDates.thirtyDaysAgoDate;
      const endDt =
        params?.endDt ||
        (parsedQuery.endDt as string) ||
        memoizedDates.todayDate;

      const queryParams = {
        franchiseId,
        startDt,
        endDt,
      };

      updateUrlWithParams(queryParams, navigate);
    },
    [location.search, navigate, memoizedDates],
  );

  // 가맹점 필터 변경 시 URL 업데이트
  const handleFranchiseChangeFromFilter = useCallback(
    (franchiseId: string) => {
      const parsedQuery = queryString.parse(location.search);
      const startDt =
        (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
      const endDt =
        (parsedQuery.endDt as string) || memoizedDates.todayDate;

      const queryParams = {
        startDt,
        endDt,
        franchiseId,
      };

      updateUrlWithParams(queryParams, navigate);
    },
    [location.search, navigate, memoizedDates],
  );

  // URL 변경 감지하여 데이터 로드
  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const startDt =
      (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
    const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
    const franchiseId = (parsedQuery.franchiseId as string) || '';

    getBokbiStatistics({ startDt, endDt, franchiseId });
    getOrderStatistics(startDt, endDt, franchiseId);
  }, [location.search, getBokbiStatistics, getOrderStatistics, memoizedDates]);

  // 관리자인 경우 프랜차이즈 목록 조회
  useEffect(() => {
    if (isAdmin) {
      getFranchise({ page: 0, size: 1000 });
    }
  }, [isAdmin, getFranchise]);

  // 기간별 데이터 필터링
  const periodData = useMemo(() => {
    if (!bokbiStatistics || !bokbiStatistics.length) return [];

    const today = new Date();
    let startDate = new Date();

    switch (selectedPeriod) {
      case 'week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case '90days':
        startDate.setDate(today.getDate() - 90);
        break;
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
    }

    return bokbiStatistics.filter(item => {
      if (!item || !item.createdAt) {
        return false;
      }
      const itemDate = new Date(item.createdAt);
      return !isNaN(itemDate.getTime()) && itemDate >= startDate && itemDate <= today;
    });
  }, [bokbiStatistics, selectedPeriod]);

  // 날짜별 펼치기/접기 토글
  const toggleDateExpansion = useCallback((e: React.MouseEvent, date: string) => {
    e.preventDefault();
    e.stopPropagation();

    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  }, []);

  // 전체 펼치기/접기
  const toggleAllExpansion = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (expandedDates.size === orderStatistics.length) {
        setExpandedDates(new Set());
      } else {
        setExpandedDates(new Set(orderStatistics.map(stat => stat.confirmDt)));
      }
    },
    [expandedDates.size, orderStatistics],
  );

  return (
    <div className="!flex !flex-col !h-full !gap-6 !p-6">
      {/* 필터 영역 */}
      <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-4">
        <BokbiStatisticsFilter
          getFilteredBokbiData={getFilterdBokbiData}
          onFranchiseChange={handleFranchiseChangeFromFilter}
        />
      </div>

      {/* 차트 영역 */}
      {getBokbiStatisticsLoading ? (
        <div className="!flex-1 !grid !grid-cols-1 lg:!grid-cols-2 !gap-6">
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6 !flex !justify-center !items-center !min-h-[484px]">
            <p className="!text-gray-500">차트 로딩중...</p>
          </div>
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6 !flex !justify-center !items-center !min-h-[484px]">
            <p className="!text-gray-500">차트 로딩중...</p>
          </div>
        </div>
      ) : (
        <div className="!flex-1 !grid !grid-cols-1 lg:!grid-cols-2 !gap-6">
          {/* 메인 차트 (좌측) */}
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6">
            <h3 className="!text-lg !font-semibold !text-gray-800 !mb-4">
              복비 충전/사용 추이
            </h3>
            <div className="!h-[400px]">
              <BokbiBarChart bokbiStatistics={bokbiStatistics} />
            </div>
          </div>

          {/* 파이 차트 및 통계 (우측) */}
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6">
            <div className="!mb-4">
              <h3 className="!text-lg !font-semibold !text-gray-800 !mb-2">
                기간별 충전/사용 비율
              </h3>
              {/* 기간 선택 탭 */}
              <div className="!flex !gap-2 !mb-4">
                {[
                  { key: 'week', label: '주간' },
                  { key: 'month', label: '월간' },
                  { key: '90days', label: '90일' },
                  { key: 'year', label: '연간' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={e => {
                      e.preventDefault();
                      setSelectedPeriod(key as any);
                    }}
                    className={`!px-4 !py-2 !rounded-lg !text-sm !font-medium !transition-colors ${
                      selectedPeriod === key
                        ? '!bg-blue-500 !text-white'
                        : '!bg-gray-100 !text-gray-700 hover:!bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 선택된 기간의 파이 차트 */}
            <div className="!h-[300px]">
              <BokbiPieChart
                annualRawData={periodData}
                title={
                  selectedPeriod === 'week'
                    ? '주간'
                    : selectedPeriod === 'month'
                    ? '월간'
                    : selectedPeriod === '90days'
                    ? '90일간'
                    : '연간'
                }
                period={selectedPeriod}
              />
            </div>

            {/* 요약 통계 */}
            <div className="!mt-6 !grid !grid-cols-2 !gap-4">
              <div className="!p-4 !bg-blue-50 !rounded-lg">
                <p className="!text-sm !text-gray-600">총 충전액</p>
                <p className="!text-xl !font-bold !text-blue-600">
                  {periodData
                    .reduce((sum, item) => sum + item.amount.CHARGE, 0)
                    .toLocaleString()}
                  원
                </p>
              </div>
              <div className="!p-4 !bg-red-50 !rounded-lg">
                <p className="!text-sm !text-gray-600">총 사용액</p>
                <p className="!text-xl !font-bold !text-red-600">
                  {periodData
                    .reduce((sum, item) => sum + item.amount.ORDER_PAY, 0)
                    .toLocaleString()}
                  원
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 주문 통계 표 영역 */}
      <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6">
        <div className="!flex !justify-between !items-center !mb-4">
          <h3 className="!text-lg !font-semibold !text-gray-800">
            일자별 주문 통계
          </h3>
          {isAdmin && (
            <p className="!text-sm !text-gray-500">
              6 Luna 고정 단가 기준 추정 · 기준 환율 (1$):{' '}
              <span className="!font-medium !text-gray-700">
                {KRW_EXCHANGE_RATE.toLocaleString()}원
              </span>
            </p>
          )}
        </div>

        {isLoadingStatistics ? (
          <div className="!flex !justify-center !items-center !py-8">
            <div className="!animate-spin !rounded-full !h-8 !w-8 !border-b-2 !border-blue-500"></div>
          </div>
        ) : orderStatistics.length > 0 ? (
          <div className="!overflow-x-auto">
            <table className="!w-full !text-sm">
              <thead>
                <tr className="!bg-gray-50 !border-b !border-gray-200">
                  <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700">
                    <button
                      onClick={toggleAllExpansion}
                      className="!flex !items-center !gap-1 hover:!text-blue-600 !transition-colors"
                    >
                      <svg
                        className={`!w-4 !h-4 !transition-transform ${
                          expandedDates.size === orderStatistics.length
                            ? '!rotate-90'
                            : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      날짜
                    </button>
                  </th>
                  <th className="!text-left !px-4 !py-3 !font-semibold !text-gray-700">
                    상품명
                  </th>
                  <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                    주문 수
                  </th>
                  {isAdmin && (
                    <>
                      <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                        총비용 (원, 추정)
                      </th>
                      <th className="!text-right !px-4 !py-3 !font-semibold !text-gray-700">
                        평균 단가 (원, 추정)
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {orderStatistics.map((stat, index) => {
                  const isExpanded = expandedDates.has(stat.confirmDt);
                  const totalDailyCostKRW = costInKrw(stat);
                  const averageDailyCostKRW = averageCost(totalDailyCostKRW, stat.totalCount);

                  return (
                    <React.Fragment key={`group-${index}`}>
                      {/* 날짜별 요약 행 */}
                      <tr
                        key={`${index}-summary`}
                        className="!border-b !border-gray-200 !bg-gray-50 hover:!bg-gray-100 !cursor-pointer"
                        onClick={e => toggleDateExpansion(e, stat.confirmDt)}
                      >
                        <td className="!px-4 !py-3 !font-medium !text-gray-900">
                          <div className="!flex !items-center !gap-2">
                            <svg
                              className={`!w-4 !h-4 !transition-transform ${
                                isExpanded ? '!rotate-90' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                            {stat.confirmDt}
                          </div>
                        </td>
                        <td className="!px-4 !py-3 !text-gray-600 !italic">
                          {`${stat.statisticsList.length}개 상품`}
                        </td>
                        <td className="!px-4 !py-3 !text-right !font-bold !text-blue-600">
                          {stat.totalCount.toLocaleString()}
                        </td>
                        {isAdmin && (
                          <>
                            <td className="!px-4 !py-3 !text-right !font-medium !text-gray-800">
                              {formatCost(totalDailyCostKRW)}
                            </td>
                            <td className="!px-4 !py-3 !text-right !font-medium !text-gray-800">
                              {formatCost(averageDailyCostKRW)}
                            </td>
                          </>
                        )}
                      </tr>
                      {/* 상품별 상세 행 (펼쳐졌을 때만 표시) */}
                      {isExpanded &&
                        stat.statisticsList.map((product, productIndex) => {
                          const totalProductCostKRW = costInKrw(product);
                          const averageProductCostKRW = averageCost(totalProductCostKRW, product.totalCount);
                          return (
                            <tr
                              key={`${index}-${productIndex}`}
                              className="!border-b !border-gray-100 hover:!bg-gray-50"
                            >
                              <td className="!px-4 !py-3 !pl-12 !text-gray-700">
                                -
                              </td>
                              <td className="!px-4 !py-3 !text-gray-700">
                                {product.productName}
                              </td>
                              <td className="!px-4 !py-3 !text-right !text-gray-700">
                                {product.totalCount.toLocaleString()}
                              </td>
                              {isAdmin && (
                                <>
                                  <td className="!px-4 !py-3 !text-right !text-gray-700">
                                    {formatCost(totalProductCostKRW)}
                                  </td>
                                  <td className="!px-4 !py-3 !text-right !text-gray-700">
                                    {formatCost(averageProductCostKRW)}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="!bg-gray-100 !border-t-2 !border-gray-300">
                  <td
                    colSpan={2}
                    className="!px-4 !py-3 !font-bold !text-gray-800 !text-right"
                  >
                    총 합계
                  </td>
                  <td
                    className={`!px-4 !py-3 !text-right !font-bold ${
                      isAdmin ? '!text-gray-800' : '!text-blue-700'
                    } !text-base`}
                  >
                    {orderStatistics
                      .reduce((sum, stat) => sum + stat.totalCount, 0)
                      .toLocaleString()}
                  </td>
                  {isAdmin && (
                    <>
                      <td className="!px-4 !py-3 !text-right !font-bold !text-red-600 !text-base">
                        {formatCost(sumCosts(orderStatistics))}
                      </td>
                      <td className="!px-4 !py-3 !text-right !font-bold !text-red-600 !text-base">
                        {formatCost(averageCost(sumCosts(orderStatistics),
                          orderStatistics.reduce((sum, stat) => sum + stat.totalCount, 0)))}
                      </td>
                    </>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="!text-center !py-8 !text-gray-500">
            조회된 통계 데이터가 없습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default BokbiStatistics;
